import { Avatar, Flex, Text } from '@chakra-ui/react';
import { useState, MutableRefObject, useRef } from 'react';
import { useLocalization } from '~/framework';
import { useDebounced } from '@dozuki/web-js/components';
import { useTeamsSearch, MINIMUM_SEARCH_TERM_LENGTH } from '../Courses/_shared/useTeamsSearch';
import {
   Catalog,
   CatalogItem,
   CatalogItemContent,
   CatalogItemHeader,
   InfiniteScrollForIncrementalQuery,
   SearchInput,
} from '~/ui-components';
import { GetTeamsOrderByOption, Image } from '~/monolith-api';

const BLUR_DELAY = 250;

export type TeamData = {
   teamid: number;
   name: string;
   image: Image;
   avatarUrl?: string;
   imageUrl?: string;
};

export type RecentTeams = TeamData[];

export type TeamSearchInputProps = {
   onChange: (teamid: number, name: string, avatarUrl?: string) => void;
   refFocus?: MutableRefObject<null> | null;
   recentTeams?: RecentTeams | null;
   ownerTeamUserid?: number;
   displayTeams?: 'whenActive' | 'always';
   orderBy?: GetTeamsOrderByOption;
};

export function TeamSearchInput({
   onChange,
   refFocus,
   recentTeams,
   ownerTeamUserid,
   displayTeams = 'always',
   orderBy = 'name',
}: TeamSearchInputProps) {
   const [searchTerm, setSearchTerm] = useState('');
   const [isActive, setIsActive] = useState(false);
   const scheduledBlur = useRef<ReturnType<typeof setTimeout> | null>(null);
   const debouncedSearchTerm = useDebounced(searchTerm, 500);
   const teamsQuery = useTeamsSearch(debouncedSearchTerm, 5, ownerTeamUserid, orderBy);
   const { flattenedData: teams } = teamsQuery;
   const { _js } = useLocalization();

   function handleFocus() {
      if (scheduledBlur.current) clearTimeout(scheduledBlur.current);
      setIsActive(true);
   }

   function handleBlur() {
      scheduledBlur.current = setTimeout(() => {
         setIsActive(false);
      }, BLUR_DELAY);
   }

   function handleTeamClick(team: TeamData) {
      onChange(team.teamid, team.name, getTeamImageURL(team));
      setIsActive(false);
   }

   function getTeamImageURL(team: TeamData) {
      return team.image?.thumbnail ?? team.avatarUrl ?? team.imageUrl;
   }

   return (
      <Flex direction="column" mt={4}>
         <SearchInput
            placeholder={_js('Search Teams')}
            name="search-teams"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            refFocus={refFocus}
            onFocus={handleFocus}
            onBlur={handleBlur}
         />
         {(isActive || displayTeams === 'always') && (
            <>
               {debouncedSearchTerm.length < 1 && recentTeams && (
                  <>
                     <Text as="b" mt={3}>
                        {_js('Recent Teams')}
                     </Text>
                     <Catalog mb={4}>
                        {Object.values(recentTeams ?? {}).map(team => (
                           <CatalogItem
                              key={team.teamid}
                              onClick={() => handleTeamClick(team)}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                           >
                              <Avatar src={getTeamImageURL(team)} />
                              <CatalogItemContent>
                                 <CatalogItemHeader>{team.name}</CatalogItemHeader>
                              </CatalogItemContent>
                           </CatalogItem>
                        ))}
                     </Catalog>
                     <Text as="b">{_js('Teams')}</Text>
                  </>
               )}
               <InfiniteScrollForIncrementalQuery
                  query={teamsQuery}
                  contained
                  maxHeight={300}
                  minHeight={100}
               >
                  {teams?.length === 0 && (
                     <Flex flex={1} flexDirection="column" align="center" justify="center">
                        <Text variant="subtitle">{_js('There are no teams to display')}</Text>
                        {debouncedSearchTerm.length >= MINIMUM_SEARCH_TERM_LENGTH && (
                           <Text variant="muted">
                              {_js('Try searching for something different.')}
                           </Text>
                        )}
                     </Flex>
                  )}
                  <Catalog>
                     {teams?.map(team => (
                        <CatalogItem
                           key={team.teamid}
                           onClick={() => handleTeamClick(team)}
                           onFocus={handleFocus}
                           onBlur={handleBlur}
                        >
                           <Avatar src={getTeamImageURL(team)} />
                           <CatalogItemContent>
                              <CatalogItemHeader>{team.name}</CatalogItemHeader>
                           </CatalogItemContent>
                        </CatalogItem>
                     ))}
                  </Catalog>
               </InfiniteScrollForIncrementalQuery>
            </>
         )}
      </Flex>
   );
}
